import { useState } from 'react';
import { Sheet } from '../components/Sheet.jsx';
import { Icon } from '../components/Icon.jsx';
import { useModulAktion } from '../hooks/data.js';
import { useToast } from '../state/toast.jsx';
import { ZUSTAND, merkeBeantragt } from './zustand.js';

const DATUM = new Intl.DateTimeFormat('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
const datum = (iso) => (iso ? DATUM.format(new Date(iso)) : '');

/**
 * Ein Modul im Detail: Beschreibung, Preis, Voraussetzungen und – je nach Zustand und Rolle – Einschalten,
 * Beantragen (mit Nachricht), Anfrage zurückziehen, Ausschalten oder Interesse melden.
 */
export function AppSheet({ modul: x, darfSchalten, darfBeantragen, online, onClose }) {
  const aktion = useModulAktion();
  const toast = useToast();
  const [nachricht, setNachricht] = useState('');
  const [fehler, setFehler] = useState('');
  const [text, art] = ZUSTAND[x.zustand] ?? ZUSTAND.bald;

  const tun = async (eingabe, meldung) => {
    setFehler('');
    try {
      await aktion.mutateAsync({ id: x.id, ...eingabe });
      if (eingabe.art === 'beantragen') merkeBeantragt(x.id);
      toast({ text: meldung });
      if (eingabe.art !== 'interesse' && eingabe.art !== 'keinInteresse') onClose();
    } catch (err) {
      setFehler(err.message);
    }
  };
  const busy = aktion.isPending || !online;
  const selbst = x.freigabe === 'selbst' || x.freigegeben;

  return (
    <Sheet title={x.titel} onClose={onClose}>
      <div className="row" style={{ gap: 12 }}>
        <span className="mehrico"><Icon name={x.icon} size={26} stroke={2.2} /></span>
        <span className="stack" style={{ gap: 4, flex: 1, minWidth: 0 }}>
          <span><span className={`pill ${art}`}>{text}</span></span>
          <span className="sub" style={{ fontSize: 13 }}>{x.preis === 'inklusive' ? 'Im Abo inbegriffen' : `Preis: ${x.preis}`}</span>
        </span>
      </div>
      <p style={{ fontSize: 15, lineHeight: 1.5 }}>{x.beschreibung}</p>
      {x.braucht.length > 0 && <p className="sub" style={{ fontSize: 13 }}>Braucht: {x.braucht.map((b) => b.titel).join(', ')}</p>}

      {x.abgelehnt && x.zustand === 'verfuegbar' && (
        <div className="notice" role="status">Anfrage am {datum(x.abgelehnt.seit)} nicht freigeschaltet{x.abgelehnt.antwort ? `: «${x.abgelehnt.antwort}»` : '.'}</div>
      )}
      {x.zustand === 'an' && x.antwort && <div className="notice info" role="status">ki|partner: «{x.antwort}»</div>}
      {x.zustand === 'beantragt' && (
        <div className="notice info" role="status">Beantragt am {datum(x.beantragt.seit)} von {x.beantragt.von}. ki|partner meldet sich – sobald es freigeschaltet ist, erscheint es in der App.</div>
      )}
      {x.zustand === 'nicht-verfuegbar' && <div className="notice" role="status">Zuerst braucht es: {x.braucht.map((b) => b.titel).join(', ')}.</div>}
      {!online && <div className="notice info" role="status">Offline – Änderungen gehen wieder mit Verbindung.</div>}
      {fehler && <div className="notice err" role="alert">{fehler}</div>}

      {x.zustand === 'an' && darfSchalten && (
        <button type="button" className="btn secondary lg block" disabled={busy} onClick={() => tun({ art: 'schalten', an: false }, `${x.titel} ist ausgeschaltet – Daten bleiben erhalten`)}>Ausschalten</button>
      )}
      {x.zustand === 'verfuegbar' && darfSchalten && selbst && (
        <button type="button" className="btn lg block" disabled={busy} onClick={() => tun({ art: 'schalten', an: true }, `${x.titel} ist eingeschaltet`)}>
          <Icon name="check" size={20} stroke={2.4} />Einschalten
        </button>
      )}
      {x.zustand === 'verfuegbar' && darfBeantragen && !(darfSchalten && selbst) && (
        <>
          <label className="field">
            <span className="label">Nachricht an ki|partner (freiwillig)</span>
            <textarea className="input" rows={3} maxLength={500} value={nachricht} onChange={(e) => setNachricht(e.target.value)} placeholder="z. B. ab wann, für welche Teams" />
          </label>
          <button type="button" className="btn lg block" disabled={busy} onClick={() => tun({ art: 'beantragen', nachricht }, 'Beantragt – ki|partner ist informiert')}>Beantragen</button>
        </>
      )}
      {x.zustand === 'verfuegbar' && !darfBeantragen && <p className="sub" style={{ fontSize: 13 }}>Einschalten oder beantragen können Projektleiter und Firmenadmins.</p>}
      {x.zustand === 'beantragt' && darfBeantragen && (
        <button type="button" className="btn secondary block" disabled={busy} onClick={() => tun({ art: 'zurueckziehen' }, 'Anfrage zurückgezogen')}>Anfrage zurückziehen</button>
      )}
      {x.zustand === 'bald' && (
        <>
          <p className="sub" style={{ fontSize: 13 }}>
            In Entwicklung. {x.interesse ? `${x.interesse} ${x.interesse === 1 ? 'Person' : 'Personen'} aus eurer Firma ${x.interesse === 1 ? 'interessiert sich' : 'interessieren sich'} dafür.` : 'Meldet Interesse – so wissen wir, was ihr zuerst braucht.'}
          </p>
          {x.meinInteresse
            ? <button type="button" className="btn secondary block" disabled={busy} onClick={() => tun({ art: 'keinInteresse' }, 'Interesse zurückgenommen')}>Interesse zurücknehmen</button>
            : <button type="button" className="btn lg block" disabled={busy} onClick={() => tun({ art: 'interesse' }, 'Danke – Interesse gemeldet')}>Interesse melden</button>}
        </>
      )}
    </Sheet>
  );
}
